<script>
/**
 * Le reglage des champs obligatoires, en grand.
 *
 * C'etait une liste deroulante accrochee au bouton, et elle ne pouvait pas
 * marcher: `.list-card` porte `overflow: hidden` - ce qui est juste, une carte
 * ne laisse pas deborder son tableau - et le menu, positionne en absolu, s'y
 * faisait couper. Le remede n'est pas de percer la carte: un menu de 220px de
 * haut etait de toute facon trop etroit pour choisir parmi plus de cent champs
 * ranges en six sections.
 *
 * D'ou une fenetre, et deux niveaux de recherche qui suivent la facon dont on
 * cherche vraiment: d'abord la section - on sait dans quelle partie du
 * questionnaire on veut exiger quelque chose - puis le champ a l'interieur.
 * Les deux restent utilisables seuls: « Toutes les sections » plus une
 * recherche libre retrouve un champ dont on ne sait plus ou il est range.
 *
 * Elle montre TOUS les champs, obligatoires compris, et se regle dans les deux
 * sens. Ne lister que les champs libres obligeait a fermer la fenetre pour en
 * retirer un, puis a la rouvrir pour en marquer un autre - alors que c'est le
 * meme geste, et souvent la meme minute: on arbitre ce que le questionnaire
 * exige, on ne fait pas l'un puis l'autre.
 *
 * La fenetre ne parle pas a l'API: elle annonce le champ touche, et la carte
 * qui la possede tient le brouillon. Une fenetre qui enregistrerait elle-meme
 * aurait sa propre idee de l'etat, a cote de celle de la liste - et surtout
 * elle ecrirait, alors que rien ne s'ecrit ici avant « Enregistrer ». Les
 * champs qu'elle recoit portent donc deja l'etat du brouillon, et `pending`
 * dit lesquels attendent d'etre enregistres.
 */
export default {
  name: 'MarkRequiredFieldModal',
  props: {
    modelValue: { type: Boolean, default: false },
    /** Tout le catalogue: obligatoires et libres, dans l'ordre des sections. */
    fields: { type: Array, default: () => [] },
    /** Cles en cours d'enregistrement, pour ne pas regler deux fois. */
    saving: { type: Array, default: () => [] },
    /** Ce que la fenetre rappelle en pied: rien ne part avant enregistrement. */
    pendingLabel: { type: String, default: '' },
  },
  emits: ['update:modelValue', 'toggle'],
  data() {
    return {
      /** `null` = toutes les sections. */
      step: null,
      search: '',
    };
  },
  computed: {
    isVisible: {
      get() {
        return this.modelValue;
      },
      set(value) {
        this.$emit('update:modelValue', value);
      },
    },

    /**
     * Les sections, avec ce qu'elles exigent sur ce qu'elles portent.
     *
     * « 3/11 » repond a la question qu'on se pose en ouvrant la fenetre - ou
     * en reste-t-il a exiger ? - la ou un simple total ne dirait rien.
     */
    sections() {
      const bySection = [];

      this.fields.forEach((field) => {
        let section = bySection.find((item) => item.step === field.step);

        if (!section) {
          section = {
            step: field.step,
            name: field.sectionLabel || `Section ${field.step}`,
            required: 0,
            total: 0,
          };
          bySection.push(section);
        }

        section.total += 1;

        if (field.isRequired) {
          section.required += 1;
        }
      });

      return bySection;
    },

    requiredCount() {
      return this.fields.filter((field) => field.isRequired).length;
    },

    /**
     * Les champs affiches: ceux de la section choisie, puis ceux que la
     * recherche retient. Le libelle et la cle se cherchent tous les deux - la
     * cle est ce qu'on a sous les yeux quand on arrive d'un message d'erreur.
     */
    visible() {
      const needle = this.search.trim().toLowerCase();

      return this.fields.filter((field) => {
        if (this.step !== null && field.step !== this.step) {
          return false;
        }

        if (!needle) {
          return true;
        }

        return (
          field.label.toLowerCase().includes(needle) ||
          field.key.toLowerCase().includes(needle)
        );
      });
    },
  },
  watch: {
    /** Chaque ouverture repart de zero: le tri d'hier n'est pas une memoire. */
    modelValue(open) {
      if (open) {
        this.step = null;
        this.search = '';
      }
    },
  },
  methods: {
    isSaving(field) {
      return this.saving.includes(field.key);
    },

    selectSection(step) {
      this.step = step;
    },

    /**
     * Le sous-titre d'une ligne: sa section, son etat, et le rappel qu'un
     * changement n'est pas encore ecrit.
     */
    metaOf(field) {
      const parts = [field.sectionLabel];

      if (field.isRequired) {
        parts.push('Obligatoire');
      }

      if (field.pending) {
        parts.push('à enregistrer');
      }

      return parts.join(' · ');
    },

    toggle(field) {
      if (field.locked || this.isSaving(field)) {
        return;
      }

      this.$emit('toggle', field);
    },
  },
};
</script>

<template>
  <BModal
    v-model="isVisible"
    modal-class="zoomIn"
    hide-footer
    centered
    size="lg"
    title="Marquer les champs obligatoires"
  >
    <p class="mark-field__lead">
      Ces champs existent déjà dans le questionnaire. Les marquer ne les crée
      pas : cela les rend obligatoires à la saisie.
      <strong>{{ requiredCount }}</strong> sur {{ fields.length }} le sont. Les
      changements s’appliqueront après enregistrement.
    </p>

    <div class="field-browser">
      <!-- D'abord la section: on sait dans quelle partie du questionnaire on
           veut exiger quelque chose, avant de savoir quel champ. -->
      <nav
        class="field-browser__sections"
        aria-label="Sections du questionnaire"
      >
        <button
          type="button"
          class="field-browser__section"
          :class="{ 'field-browser__section--active': step === null }"
          data-testid="mark-field-section-all"
          @click="selectSection(null)"
        >
          <span>Toutes les sections</span>
          <span class="field-browser__section-count">
            {{ requiredCount }}/{{ fields.length }}
          </span>
        </button>

        <button
          v-for="section in sections"
          :key="section.step"
          type="button"
          class="field-browser__section"
          :class="{ 'field-browser__section--active': step === section.step }"
          :data-testid="`mark-field-section-${section.step}`"
          @click="selectSection(section.step)"
        >
          <span>{{ section.name }}</span>
          <span class="field-browser__section-count">
            {{ section.required }}/{{ section.total }}
          </span>
        </button>
      </nav>

      <div class="field-browser__panel">
        <div class="field-browser__search">
          <i class="ri-search-line"></i>
          <input
            v-model="search"
            type="search"
            class="list-input"
            placeholder="Rechercher un champ…"
            aria-label="Rechercher un champ"
            data-testid="mark-field-search"
          />
        </div>

        <p v-if="!visible.length" class="field-browser__empty">
          Aucun champ ne correspond.
        </p>

        <ul v-else class="field-browser__list">
          <li v-for="field in visible" :key="field.key">
            <button
              type="button"
              class="field-browser__row field-browser__row--clickable"
              :disabled="field.locked || isSaving(field)"
              :title="
                field.locked
                  ? field.lockedReason
                  : field.isRequired
                    ? `Retirer « ${field.label} » des champs obligatoires`
                    : `Marquer « ${field.label} » obligatoire`
              "
              :data-testid="`mark-field-pick-${field.key}`"
              @click="toggle(field)"
            >
              <span class="field-browser__text">
                <span class="field-browser__label">{{ field.label }}</span>
                <span class="field-browser__meta">{{ metaOf(field) }}</span>
              </span>

              <span v-if="field.locked" class="field-browser__locked">
                <i class="ri-lock-line"></i>
                Verrouillé
              </span>
              <span
                v-else-if="field.isRequired"
                class="field-browser__action field-browser__action--danger"
              >
                <i class="ri-indeterminate-circle-line"></i>
                Retirer
              </span>
              <span v-else class="field-browser__action">
                <i class="ri-add-line"></i>
                Marquer
              </span>
            </button>
          </li>
        </ul>
      </div>
    </div>

    <footer class="modal-footer-actions">
      <span v-if="pendingLabel" class="mark-field__pending">
        <i class="ri-information-line"></i>
        {{ pendingLabel }} — à enregistrer depuis la page.
      </span>

      <button
        type="button"
        class="modal-btn modal-btn--primary"
        data-testid="mark-field-close"
        @click="isVisible = false"
      >
        Fermer
      </button>
    </footer>
  </BModal>
</template>

<style scoped>
.mark-field__lead {
  margin: 0 0 16px;
  font-size: 13px;
  color: #6c757d;
}

/* Le rappel se pose a gauche du bouton, dans l'ambre de la barre de la carte:
   c'est le meme avertissement, au meme endroit du regard. */
.mark-field__pending {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-right: auto;
  font-size: 13px;
  color: #8a5a00;
}
</style>
